import { useCallback, useMemo } from 'react'
import type { LiveChannelState, PaginatedUsers } from '@harmony/live-types'
import { useMyUserId, useSpaceDataWithId } from 'use-towns-client'

import {
    useLiveAudioManager,
    useLiveAudioState,
    useLiveChannelState,
    useLiveClient,
    useLiveUserDevicesState,
} from './LiveClientProvider'

/**
 * Merges stageRequests users into listeners to compute the true "audience"
 * Users requesting to join stage should remain in the audience count until
 * they're actually on stage. This preserves total participant counts.
 */
function mergeAudienceWithStageRequests(channelState: LiveChannelState): LiveChannelState {
    const { listeners, stageRequests } = channelState

    // Merge userIds from both arrays, avoiding duplicates
    const mergedUserIds = [...listeners.userIds]
    for (const userId of stageRequests.userIds) {
        if (!mergedUserIds.includes(userId)) {
            mergedUserIds.push(userId)
        }
    }

    // Create merged listeners object
    const mergedListeners: PaginatedUsers = {
        userIds: mergedUserIds,
        totalCount: listeners.totalCount + stageRequests.totalCount,
        cursor: listeners.cursor, // Keep original cursor for pagination
    }

    return {
        ...channelState,
        listeners: mergedListeners,
    }
}

/**
 * Shared hook for live session state and actions.
 * Used by both desktop LiveSessionFooter and mobile MobileLiveAudioBar.
 */
export function useLiveSessionState(componentName: string) {
    const userId = useMyUserId()
    const devicesState = useLiveUserDevicesState()
    const audioState = useLiveAudioState()
    const audioManager = useLiveAudioManager()
    const { leaveChannel, leaveStage, joinStage, joinChannel, moderateStage } = useLiveClient()

    const { devices, deviceState } = devicesState ?? {}

    // Current device is live
    const isCurrentDeviceLive = deviceState?.type === 'live'

    // Find another device that is live
    const otherLiveDevice = useMemo(() => {
        if (!devices || !deviceState) {
            return undefined
        }
        return devices.find((d) => d.type === 'live' && d.deviceId !== deviceState.deviceId)
    }, [devices, deviceState])

    // Get the live device info (either current or other)
    const liveDevice = isCurrentDeviceLive ? deviceState : otherLiveDevice

    // Get space data for the live session
    const spaceId = liveDevice?.type === 'live' ? liveDevice.spaceId : undefined
    const channelId = liveDevice?.type === 'live' ? liveDevice.channelId : undefined
    const spaceData = useSpaceDataWithId(spaceId, componentName)

    // Get raw channel state from server
    const rawChannelState = useLiveChannelState(spaceId, channelId)

    // Compute channel state with merged audience (listeners + stageRequests)
    // This ensures users requesting stage remain in the audience count
    const channelState = useMemo(() => {
        if (!rawChannelState) {
            return undefined
        }
        return mergeAudienceWithStageRequests(rawChannelState)
    }, [rawChannelState])

    // Find channel name from space data
    const channelName = useMemo(() => {
        if (!spaceData || !channelId) {
            return undefined
        }
        for (const group of spaceData.channelGroups ?? []) {
            const channel = group.channels.find((c) => c.id === channelId)
            if (channel) {
                return channel.label
            }
        }
        return undefined
    }, [spaceData, channelId])

    const spaceName = spaceData?.name

    // Check if user is on stage
    const isOnStage = useMemo(() => {
        if (!channelState || !deviceState || !userId) {
            return false
        }
        return channelState.stage.userIds.some((id) => id === userId)
    }, [channelState, deviceState, userId])

    // Check if user is requesting to join stage
    const isRequestingToJoinStage = useMemo(() => {
        if (!channelState || !deviceState || !userId) {
            return false
        }
        return channelState.stageRequests.userIds.some((id) => id === userId)
    }, [channelState, deviceState, userId])

    // Derived state
    const isLive = isCurrentDeviceLive && !!channelState
    const hasListeners = (channelState?.listeners.totalCount ?? 0) > 0
    const hasStageRequests = (channelState?.stageRequests?.totalCount ?? 0) > 0
    const isAudioConnected = audioState?.status === 'connected'

    // Connection status for SoundWaveIcon
    const connectionStatus = useMemo((): 'connected' | 'connecting' | 'error' => {
        if (audioState?.status === 'error') {
            return 'error'
        }
        if (audioState?.status === 'connected') {
            return 'connected'
        }
        // initializing or undefined
        return 'connecting'
    }, [audioState?.status])

    // Handlers
    const handleLeave = useCallback(() => {
        leaveChannel()
    }, [leaveChannel])

    const handleListen = useCallback(() => {
        leaveStage()
    }, [leaveStage])

    const handleJoinStage = useCallback(() => {
        joinStage()
    }, [joinStage])

    const handleSwitchHere = useCallback(() => {
        if (otherLiveDevice?.type === 'live') {
            joinChannel(otherLiveDevice.spaceId, otherLiveDevice.channelId)
        }
    }, [otherLiveDevice, joinChannel])

    const handleModerateStage = useCallback(
        (action: import('@harmony/live-types').ModerateStageAction) => {
            if (channelId) {
                moderateStage(channelId, action)
            }
        },
        [channelId, moderateStage],
    )

    const handleToggleMute = useCallback(
        (explicitValue?: boolean) => {
            const nextValue = explicitValue ?? !audioState?.localMuted
            audioManager?.setMuted(nextValue)
        },
        [audioManager, audioState?.localMuted],
    )

    const handleSetPreferredDevice = useCallback(
        (device: MediaDeviceInfo) => {
            audioManager?.setPreferredDevice(device)
        },
        [audioManager],
    )

    const handleSetOutputDevice = useCallback(
        (device: MediaDeviceInfo) => {
            audioManager?.setOutputDevice(device.deviceId)
        },
        [audioManager],
    )

    return {
        // IDs
        userId,
        spaceId,
        channelId,

        // Names
        spaceName,
        channelName,

        // Device state
        deviceState,
        isCurrentDeviceLive,
        otherLiveDevice,

        // Channel state
        channelState,

        // Audio state
        audioState,
        isAudioConnected,

        // Stage state
        isOnStage,
        isRequestingToJoinStage,

        // Derived
        isLive,
        hasListeners,
        hasStageRequests,
        connectionStatus,

        // Handlers
        handleLeave,
        handleListen,
        handleJoinStage,
        handleSwitchHere,
        handleModerateStage,
        handleToggleMute,
        handleSetPreferredDevice,
        handleSetOutputDevice,
    }
}
